import { useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { Loader2, Upload } from 'lucide-react'
import { decodeUploadFileName, mapWithConcurrencySettled } from '@numeo/types-core'
import { Button } from '@/components/ds2'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { SnackbarType } from '@/utils/snackbar'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { fetchBoxTruckProfileDocumentBlobUrl, MAX_PARALLEL_DOCUMENT_UPLOADS } from '../services/boxTruckProfilesApi'
import type { BoxTruckProfileDocumentEntity } from '../services/boxTruckDirectDocumentUpload'
import { DocumentLightbox } from './DocumentLightbox'
import { useDocumentPreview } from 'components/shared/useDocumentPreview'
import { ProfileDocumentChips } from './ProfileDocumentChips'
import { CardAutofillStatus } from './documentAutofill/CardAutofillStatus'
import { FillWithAiButton } from './documentAutofill/FillWithAiButton'
import { useDocumentAutofillContext } from './documentAutofill/DocumentAutofillContext'
import { useProfileDocumentAutofill } from '../hooks/useProfileDocumentAutofill'
import { trackBoxTruckProfileDocumentRemoval, trackBoxTruckProfileDocumentSkipped, type BoxTruckDocumentScope } from '../utils/analytics'
import { BOX_TRUCK_DOCUMENT_ACCEPT, boxTruckDocumentRejection } from '../utils/documentMimeTypes'

interface ProfileDocument {
    documentId: string
    type: string
    fileName: string
    contentType?: string
    removalIncomplete?: boolean
}

interface ProfileDocumentsProps<T extends string, D extends ProfileDocument> {
    entityId?: string
    types: readonly T[]
    labels: Record<T, string>
    documents: D[]
    onDocumentsChange?: Dispatch<SetStateAction<D[]>>
    pendingDocuments?: Partial<Record<T, File[]>>
    onPendingDocumentsChange?: Dispatch<SetStateAction<Partial<Record<T, File[]>>>>
    /** The kind of profile the documents are on: where a saved one is previewed from and read with AI. */
    entity: BoxTruckProfileDocumentEntity
    uploadDocument: (entityId: string, type: T, file: File) => Promise<D>
    removeDocument?: (entityId: string, documentId: string) => Promise<void>
    onRemovalFailure?: () => Promise<void>
    removalEntityType?: BoxTruckDocumentScope
    removalDescription?: (document: D) => string
    dropzoneCards?: boolean
    clickToChoose?: boolean
}

export const ProfileDocuments = <T extends string, D extends ProfileDocument>({
    entityId,
    types,
    labels,
    documents,
    onDocumentsChange,
    pendingDocuments = {},
    onPendingDocumentsChange,
    entity,
    uploadDocument,
    removeDocument,
    onRemovalFailure,
    removalEntityType,
    removalDescription,
    dropzoneCards = false,
    clickToChoose = false,
}: ProfileDocumentsProps<T, D>) => {
    const { showSnackbar } = useSnackbar()
    const [busyType, setBusyType] = useState<T | null>(null)
    const [removingId, setRemovingId] = useState<string | null>(null)
    const [draggingType, setDraggingType] = useState<T | null>(null)
    const inputs = useRef<Partial<Record<T, HTMLInputElement | null>>>({})
    const autofill = useDocumentAutofillContext()
    const { cardFill, fileOf, forgetFile } = useProfileDocumentAutofill<T, D>({ entity, entityId })
    const { preview, blobUrl, openPreview, closePreview } = useDocumentPreview<ProfileDocument>(() =>
        showSnackbar(SnackbarType.ERROR, 'Could not open the document')
    )

    const byType = useMemo(() => {
        const result = new Map<T, D[]>()
        for (const document of documents) {
            const type = types.find((candidate) => candidate === document.type)
            if (type) result.set(type, [...(result.get(type) ?? []), document])
        }
        return result
    }, [documents, types])

    const openDocument = (document: D) => {
        if (entityId) openPreview(document, () => fetchBoxTruckProfileDocumentBlobUrl(entity, entityId, document.documentId))
    }

    const removeStoredDocument = async (document: D) => {
        if (!entityId || !removeDocument || removingId || busyType !== null) return
        setRemovingId(document.documentId)
        try {
            await removeDocument(entityId, document.documentId)
            onDocumentsChange?.((current) => current.filter((item) => item.documentId !== document.documentId))
            const type = types.find((candidate) => candidate === document.type)
            if (type) forgetFile(type, document)
            if (removalEntityType) trackBoxTruckProfileDocumentRemoval(removalEntityType, document.type, 'success')
            showSnackbar(SnackbarType.SUCCESS, `${decodeUploadFileName(document.fileName)} removed`)
        } catch (error) {
            if (removalEntityType) trackBoxTruckProfileDocumentRemoval(removalEntityType, document.type, 'failure')
            showSnackbar(SnackbarType.ERROR, getUserErrorMessage(error) || 'Could not remove the document')
            try {
                await onRemovalFailure?.()
            } catch {
                showSnackbar(SnackbarType.ERROR, 'Could not refresh document status. Reload the page before retrying.')
            }
        } finally {
            setRemovingId(null)
        }
    }

    const removePendingFile = (type: T, file: File) => {
        forgetFile(type, file)
        onPendingDocumentsChange?.((current) => ({ ...current, [type]: (current[type] ?? []).filter((currentFile) => currentFile !== file) }))
    }

    const addFiles = async (type: T, files: File[]) => {
        if (files.length === 0) return
        if (removingId) {
            showSnackbar(SnackbarType.WARNING, 'Document removal is still in progress. Please add the files again when it finishes.')
            return
        }
        const checked = files.map((file) => ({ file, rejection: boxTruckDocumentRejection(file) }))
        const accepted = checked.filter(({ rejection }) => rejection === null).map(({ file }) => file)
        const rejected = checked.filter(({ rejection }) => rejection !== null)
        if (rejected.length > 0) {
            rejected.forEach(({ file }) => trackBoxTruckProfileDocumentSkipped(file))
            const [first] = rejected
            const others = rejected.length > 1 ? ` ${rejected.length - 1} more file(s) skipped.` : ''
            showSnackbar(SnackbarType.ERROR, `${first.file.name} was skipped: ${first.rejection}.${others}`)
        }
        if (accepted.length === 0) return
        if (!entityId) {
            onPendingDocumentsChange?.((current) => ({ ...current, [type]: [...(current[type] ?? []), ...accepted] }))
            return
        }
        setBusyType(type)
        try {
            const results = await mapWithConcurrencySettled(accepted, MAX_PARALLEL_DOCUMENT_UPLOADS, (file) => uploadDocument(entityId, type, file))
            const uploaded = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []))
            const failed = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected')
            if (uploaded.length > 0) onDocumentsChange?.((current) => [...current, ...uploaded])
            if (failed.length > 0) {
                const reason = getUserErrorMessage(failed[0].reason)
                showSnackbar(SnackbarType.ERROR, `${uploaded.length} uploaded, ${failed.length} failed${reason ? `: ${reason}` : ''}`)
            } else {
                showSnackbar(SnackbarType.SUCCESS, accepted.length === 1 ? `${labels[type]} uploaded` : `${accepted.length} files uploaded`)
            }
        } finally {
            setBusyType(null)
        }
    }

    return (
        <div
            className={
                dropzoneCards
                    ? 'grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,18rem),1fr))]'
                    : 'divide-y divide-border overflow-hidden rounded-xl border border-border'
            }
        >
            {types.map((type) => {
                const stored = byType.get(type) ?? []
                const activeStoredCount = stored.filter((document) => !document.removalIncomplete).length
                const pending = pendingDocuments[type] ?? []
                const readable = cardFill(type, stored, pending)
                // A file still uploading is not on the card yet: the button waits for it rather than saying there is no file to read.
                const fill = readable && busyType === type ? { unavailable: 'Uploading…' } : readable
                const uploadHint =
                    busyType === type
                        ? 'Uploading…'
                        : draggingType === type
                          ? 'Release to add files'
                          : `Drop ${activeStoredCount || pending.length ? 'more ' : ''}files here or ${clickToChoose ? 'click to browse' : 'choose Upload'}`
                return (
                    <div
                        key={type}
                        role="group"
                        aria-label={`${labels[type]} upload`}
                        aria-busy={busyType === type}
                        tabIndex={clickToChoose && busyType === null ? 0 : undefined}
                        className={
                            dropzoneCards
                                ? `flex min-h-28 min-w-0 items-start gap-3 rounded-xl border-2 border-dashed p-4 transition-colors ${
                                      clickToChoose && busyType === null ? 'cursor-pointer focus-visible:outline-2 focus-visible:outline-accent' : ''
                                  } ${
                                      draggingType === type
                                          ? 'border-accent bg-accent-soft'
                                          : 'border-border bg-surface-secondary hover:border-accent hover:bg-accent-soft/30'
                                  }`
                                : `flex items-center justify-between gap-3 px-4 py-3 transition-colors duration-150 hover:bg-default ${draggingType === type ? 'bg-accent-soft' : ''}`
                        }
                        onDragOver={(event) => {
                            event.preventDefault()
                            if (busyType === null && !removingId) setDraggingType(type)
                        }}
                        onDragLeave={(event) => {
                            if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setDraggingType(null)
                        }}
                        onDrop={(event) => {
                            event.preventDefault()
                            setDraggingType(null)
                            const files = Array.from(event.dataTransfer.files ?? [])
                            if (files.length === 0) return
                            if (busyType !== null) {
                                showSnackbar(SnackbarType.WARNING, 'Document upload is still in progress. Please add the files again when it finishes.')
                                return
                            }
                            void addFiles(type, files)
                        }}
                        onClick={(event) => {
                            // React portal events still bubble through this component tree. The remove
                            // confirmation lives in a portal outside the card's DOM subtree; clicking
                            // its backdrop or body must not open the file picker behind the dialog.
                            if (
                                clickToChoose &&
                                busyType === null &&
                                event.target instanceof Element &&
                                event.currentTarget.contains(event.target) &&
                                !event.target.closest('button, input')
                            ) {
                                inputs.current[type]?.click()
                            }
                        }}
                        onKeyDown={(event) => {
                            if (clickToChoose && busyType === null && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                                event.preventDefault()
                                inputs.current[type]?.click()
                            }
                        }}
                    >
                        {dropzoneCards && (
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent" aria-hidden="true">
                                <Upload className="h-5 w-5" strokeWidth={1.75} />
                            </span>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="flex items-start gap-2">
                                <p className="min-w-0 flex-1 text-sm font-medium text-foreground">{labels[type]}</p>
                                {fill && (
                                    <FillWithAiButton
                                        cardLabel={labels[type]}
                                        fields={autofill?.fieldsOf(type) ?? []}
                                        unavailable={fill.unavailable}
                                        onFill={() => fill.fill?.()}
                                    />
                                )}
                            </div>
                            <p className={`mt-1 text-xs ${dropzoneCards ? 'font-medium text-accent' : 'text-foreground-muted'}`}>{uploadHint}</p>
                            {dropzoneCards && (activeStoredCount > 0 || pending.length > 0) && (
                                <p className="mt-1 text-xs text-foreground-muted">
                                    {activeStoredCount > 0 && `${activeStoredCount} uploaded`}
                                    {activeStoredCount > 0 && pending.length > 0 && ' · '}
                                    {pending.length > 0 && `${pending.length} ready to upload when saved`}
                                </p>
                            )}
                            <ProfileDocumentChips
                                stored={stored}
                                pending={pending}
                                canRemove={Boolean(entityId && removeDocument)}
                                busy={removingId !== null || busyType !== null}
                                removingId={removingId}
                                removalDescription={removalDescription}
                                onOpen={openDocument}
                                onRemoveStored={(document) => void removeStoredDocument(document)}
                                onPreviewPending={(file, index) =>
                                    openPreview({ documentId: `pending-${index}`, type, fileName: file.name, contentType: file.type }, () =>
                                        Promise.resolve(URL.createObjectURL(file))
                                    )
                                }
                                onRemovePending={(file) => removePendingFile(type, file)}
                            />
                            <CardAutofillStatus
                                card={type}
                                types={types}
                                labels={labels}
                                fileOf={(sourceId) => fileOf(sourceId, stored, pending)}
                                onRemovePending={(file) => removePendingFile(type, file)}
                                onRemoveStored={entityId && removeDocument ? (document) => void removeStoredDocument(document) : undefined}
                                removalDescription={removalDescription}
                            />
                        </div>
                        <input
                            ref={(element) => {
                                inputs.current[type] = element
                            }}
                            type="file"
                            multiple
                            accept={BOX_TRUCK_DOCUMENT_ACCEPT}
                            className="hidden"
                            onChange={(event) => {
                                const files = Array.from(event.target.files ?? [])
                                event.target.value = ''
                                void addFiles(type, files)
                            }}
                        />
                        {!clickToChoose && (
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={busyType !== null}
                                prefix={busyType === type ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                                onClick={() => inputs.current[type]?.click()}
                            >
                                Upload
                            </Button>
                        )}
                    </div>
                )
            })}
            {preview && <DocumentLightbox fileName={preview.fileName} contentType={preview.contentType} blobUrl={blobUrl} onClose={closePreview} />}
        </div>
    )
}
